import { useEffect, useRef } from "react";
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslate } from "@refinedev/core";

import { Form } from "@shared/components";
import { EndpointFormValues } from "@modules/erpIntegrations/validations";

type FieldListName = "query" | "headers" | "body";

interface EndpointFieldListProps {
  name: FieldListName;
  title: string;
}

export function EndpointFieldList({ name, title }: EndpointFieldListProps) {
  const translate = useTranslate();
  const { control, formState: { errors } } = useFormContext<EndpointFormValues>();
  const { fields, append, remove } = useFieldArray({ control, name });
  const fieldsListRef = useRef<HTMLDivElement>(null);
  const previousFieldCount = useRef(fields.length);

  useEffect(() => {
    if (fields.length > previousFieldCount.current) {
      const list = fieldsListRef.current;
      if (list) list.scrollTop = list.scrollHeight;
    }
    previousFieldCount.current = fields.length;
  }, [fields.length]);

  useEffect(() => {
    const listErrors = errors[name];
    const list = fieldsListRef.current;
    if (!list || !Array.isArray(listErrors)) return;

    const index = listErrors.findIndex(Boolean);
    const row = list.querySelector<HTMLElement>(`[data-endpoint-field-index="${index}"]`);
    if (!row) return;

    const delta = row.getBoundingClientRect().top - list.getBoundingClientRect().top;
    if (delta < 0 || delta + row.offsetHeight > list.clientHeight) {
      list.scrollTop += delta;
    }
  }, [errors, name]);

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {title}
      </Typography>
      {fields.length > 0 && (
        <Box ref={fieldsListRef} sx={{ maxHeight: 260, overflowY: "auto", pt: 1.5, pr: 1 }}>
          <Stack spacing={1}>
            {fields.map((field, index) => (
              <Stack
                key={field.id}
                data-endpoint-field-index={index}
                direction="row"
                spacing={1}
                alignItems="flex-start"
                flexWrap="wrap"
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Form.Input
                    name={`${name}.${index}.key`}
                    label={translate("erp_integrations.endpoints.field_key")}
                    required
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Form.Input
                    name={`${name}.${index}.label`}
                    label={translate("erp_integrations.endpoints.field_label")}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Form.Input
                    name={`${name}.${index}.default`}
                    label={translate("erp_integrations.endpoints.field_default")}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Form.Input
                    name={`${name}.${index}.prefix`}
                    label={translate("erp_integrations.endpoints.field_prefix")}
                  />
                </Box>
                <Box sx={{ pt: 1, flexShrink: 0 }}>
                  <Form.Switch
                    name={`${name}.${index}.required`}
                    label={translate("erp_integrations.endpoints.field_required")}
                  />
                </Box>
                <Box sx={{ pt: 1, flexShrink: 0 }}>
                  <Form.Switch
                    name={`${name}.${index}.from_access_token`}
                    label={translate("erp_integrations.endpoints.field_from_access_token")}
                  />
                </Box>
                <IconButton
                  aria-label={translate("erp_integrations.actions.remove_field")}
                  onClick={() => remove(index)}
                  sx={{ mt: 1 }}
                >
                  <DeleteIcon />
                </IconButton>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}
      <Button
        type="button"
        size="small"
        variant="outlined"
        sx={{ mt: 1 }}
        onClick={() =>
          append({
            key: "",
            label: "",
            required: false,
            default: "",
            prefix: "",
            from_access_token: false,
          })
        }
      >
        {translate("erp_integrations.actions.add_field")}
      </Button>
    </Box>
  );
}
